import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Alert,
  Breadcrumbs,
  Link as MuiLink,
  Chip,
  Card,
  CardContent,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Fade,
  Avatar,
  Divider,
  Snackbar,
  Tooltip,
  Stack,
  useTheme,
  alpha,
  CircularProgress,
} from '@mui/material';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
} from '@mui/lab';
import {
  Description as DescriptionIcon,
  Timeline as TimelineIcon,
  NavigateNext as NavigateNextIcon,
  History as HistoryIcon,
  Close as CloseIcon,
  Create as CreateIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Upload as UploadIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  CheckCircle as ValidateIcon,
  Cancel as RejectIcon,
  Comment as CommentIcon,
  PlayArrow as ActionIcon,
  Download as DownloadIcon,
  AttachFile as FileIcon,
  Cloud as CloudIcon,
  Storage as StorageIcon,
  AccountTree as WorkflowIcon,
} from '@mui/icons-material';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import JSZip from 'jszip';
import projectsService from '../services/projectsService';
import api from '../services/authService';
import { DocumentRevision, Document as ProjectDocument, DocumentAudit, RevisionWorkflow, DocumentParty, MetadataPrecheckReusePayload, PendingAttachment, Party, User, RevisionName, CreateDocumentRevisionForm } from '../types';
import LoadingAnimation from '../components/LoadingAnimation';
import { useAuth } from '../contexts/AuthContext';
import { hasCapability, hasCapabilityOrProjectPm, CAP } from '../utils/permissions';
import RevisionActionsDialog from '../components/RevisionActionsDialog';
import DownloadSelectionDialog, { DownloadSelectionPanel, DownloadableFileItem } from '../components/DownloadSelectionDialog';
import { openFilePreviewInNewTab, triggerBlobDownload } from '../utils/filePreview';
import RevisionDialog from '../components/RevisionDialog';
import WorkflowDialog from '../components/WorkflowDialog';
import { RevisionFormValues, RevisionWorkflowFormValues } from '../components/forms/formTypes';
import { findLastDocumentWorkflow, workflowFormToSteps } from '../components/forms/workflowUtils';
import { sanitizeFilename } from '../utils/sanitize';
import {
  isRevisionStarted,
  WORKFLOW_ASSIGNMENT_CONFLICT_ERROR,
} from '../utils/revisionUtils';
import { hasWorkflowParties } from '../components/forms/formTypes';
import { isProposalProjectType } from '../utils/projectTypes';

interface RevisionCardFilesProps {
  revisionId: number;
  versionId?: number;
  projectId: number;
  documentId: number;
  loadFiles: (revisionId: number, versionId?: number) => Promise<DownloadableFileItem[]>;
  onView: (file: DownloadableFileItem) => void;
  onDownload: (file: DownloadableFileItem) => void;
  onDownloadAll: (selectedFiles: DownloadableFileItem[]) => Promise<void> | void;
  isDownloadingZip: boolean;
}

const RevisionCardFiles: React.FC<RevisionCardFilesProps> = ({
  revisionId,
  versionId,
  projectId,
  documentId,
  loadFiles,
  onView,
  onDownload,
  onDownloadAll,
  isDownloadingZip,
}) => {
  const [files, setFiles] = useState<DownloadableFileItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const fetchFiles = async () => {
      setLoading(true);
      try {
        const fileList = await loadFiles(revisionId, versionId);
        if (!cancelled) {
          setFiles(fileList);
        }
      } catch (err) {
        console.error('Error loading revision files:', err);
        if (!cancelled) {
          setFiles([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    void fetchFiles();
    return () => {
      cancelled = true;
    };
  }, [revisionId, versionId, projectId, documentId, loadFiles]);

  if (loading) {
    return (
      <Box display="flex" alignItems="center" gap={1} mt={1.5} onClick={(e) => e.stopPropagation()}>
        <CircularProgress size={14} />
        <Typography variant="caption" color="text.secondary">
          Loading files...
        </Typography>
      </Box>
    );
  }

  if (files.length === 0) {
    return null;
  }

  return (
    <Box mt={1.5} onClick={(e) => e.stopPropagation()}>
      <DownloadSelectionPanel
        files={files}
        onView={onView}
        onDownload={onDownload}
        onDownloadAll={onDownloadAll}
        isDownloadingAll={isDownloadingZip}
        compact
        showHint={false}
        title="Files"
      />
    </Box>
  );
};

const DocumentRevisionsPage: React.FC = () => {
  const { projectId, documentId } = useParams<{ projectId: string; documentId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // Nautilus Console Design System Tokens
  const panelBorder = isDark ? '#2c3e50' : '#e0e0e0';
  const surfaceBg = isDark ? '#0f2134' : '#ffffff';
  const NAUTILUS = { deepOcean: '#0f2134', sand: '#ccba9b', abyssal: '#0a1622' };

  const revisionCardButtonSx = {
    fontSize: '0.7rem',
    fontWeight: 500,
    py: 0.25,
    px: 0.75,
    minHeight: 26,
    lineHeight: 1.2,
    '& .MuiButton-startIcon': {
      marginRight: 0.35,
      '& > *:nth-of-type(1)': { fontSize: '0.85rem' },
    },
  };

  const [document, setDocument] = useState<ProjectDocument | null>(null);
  const [revisions, setRevisions] = useState<DocumentRevision[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timeseriesModalOpen, setTimeseriesModalOpen] = useState(false);
  const [auditData, setAuditData] = useState<DocumentAudit[]>([]);
  const [timeseriesLoading, setTimeseriesLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [selectedRevision, setSelectedRevision] = useState<DocumentRevision | null>(null);
  const [revisionWorkflow, setRevisionWorkflow] = useState<RevisionWorkflow | null>(null);
  const [revisionWorkflows, setRevisionWorkflows] = useState<Record<number, RevisionWorkflow>>({});
  const [revisionAuxFileCounts, setRevisionAuxFileCounts] = useState<Record<number, number>>({});
  const [actionsDialogOpen, setActionsDialogOpen] = useState(false);
  const [projectParties, setProjectParties] = useState<Party[]>([]);
  const [projectUsers, setProjectUsers] = useState<User[]>([]);
  const [projectPmId, setProjectPmId] = useState<number | null>(null);
  const [projectStatus, setProjectStatus] = useState<string>('');
  const [projectType, setProjectType] = useState<string>('');
  const [revisionNames, setRevisionNames] = useState<RevisionName[]>([]);
  const [createRevisionDialogOpen, setCreateRevisionDialogOpen] = useState(false);
  const [creatingRevision, setCreatingRevision] = useState(false);
  const [selectedRevisionCard, setSelectedRevisionCard] = useState<DocumentRevision | null>(null);
  const [editingRevision, setEditingRevision] = useState<DocumentRevision | null>(null);
  const [revisionDialogInitialForm, setRevisionDialogInitialForm] = useState<Partial<RevisionFormValues> | null>(null);
  const [workflowDialogOpen, setWorkflowDialogOpen] = useState(false);
  const [workflowRevision, setWorkflowRevision] = useState<DocumentRevision | null>(null);
  const [workflowInitialForm, setWorkflowInitialForm] = useState<RevisionWorkflowFormValues | null>(null);
  const [savingWorkflow, setSavingWorkflow] = useState(false);
  const cardsGridRef = useRef<HTMLDivElement | null>(null);
  const revisionActionButtonRef = useRef<HTMLDivElement | null>(null);

  const [zipDownloadingRevisionId, setZipDownloadingRevisionId] = useState<number | null>(null);

  // Audit history file selection dialog
  const [auditDownloadDialogOpen, setAuditDownloadDialogOpen] = useState(false);
  const [auditDownloadFiles, setAuditDownloadFiles] = useState<DownloadableFileItem[]>([]);
  const [auditDownloadContext, setAuditDownloadContext] = useState<{ revisionId: number; versionId: number } | null>(null);
  const [auditDownloadLoading, setAuditDownloadLoading] = useState(false);

  const documentIsCancelled = Boolean(document?.is_cancelled);
  const projectIsClosed = projectStatus.toLowerCase() === 'closed';
  const isProjectManagerForCurrentProject = Boolean(user && projectPmId !== null && user.id === projectPmId);
  const canCreateDocumentRevision = hasCapability(user, CAP.CREATE_DOCUMENT_REVISION);
  const canEditRevisionWorkflow = hasCapabilityOrProjectPm(
    user,
    CAP.EDIT_REVISION_WORKFLOW,
    projectPmId,
  );
  const hasAdminLikeRevisionRights = hasCapabilityOrProjectPm(
    user,
    CAP.DOCUMENT_CONTROLLER_ACTIONS,
    projectPmId,
  );

  const lastDocumentWorkflowTemplate = useMemo(() => {
    if (!workflowRevision) {
      return null;
    }
    return findLastDocumentWorkflow(revisions, revisionWorkflows, workflowRevision.id);
  }, [revisions, revisionWorkflows, workflowRevision]);
  const isLoadingDataRef = useRef(false);
  const isPollingRefreshRef = useRef(false);
  const isActionSubmittingRef = useRef(false);

  const getRevisionsSignature = useCallback((items: DocumentRevision[]) => (
    items
      .map((item) => [
        item.id ?? '',
        item.status ?? '',
        item.step ?? '',
        item.revision_counter ?? '',
        item.file_path ?? '',
      ].join('|'))
      .sort()
      .join('||')
  ), []);
  const revisionsSignatureRef = useRef<string>('');
  const deepLinkHandledRef = useRef<string | null>(null);

  const clearDeepLinkParams = useCallback(() => {
    if (searchParams.has('revisionId') || searchParams.has('dialog')) {
      const next = new URLSearchParams(searchParams);
      next.delete('revisionId');
      next.delete('dialog');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (projectId && documentId) {
      deepLinkHandledRef.current = null;
      loadData();
    }
  }, [projectId, documentId]);

  const loadData = useCallback(async (silent = false) => {
    if (!projectId || !documentId || isLoadingDataRef.current) return;

    isLoadingDataRef.current = true;
    try {
      if (!silent) {
        setLoading(true);
      }
      const [revisionsData, documentsData, partiesData, usersData, revisionNamesData] = await Promise.all([
        projectsService.getDocumentRevisions(Number(projectId), Number(documentId)),
        projectsService.getDocumentsByProject(Number(projectId)),
        projectsService.getProjectParties(Number(projectId)),
        projectsService.getProjectUsers(Number(projectId)),
        projectsService.getRevisionNames(),
      ]);
      const projectDetails = await projectsService.getProjectDetails(Number(projectId));

      const doc = documentsData.find(d => d.id === Number(documentId));
      setDocument(doc || null);
      setRevisions(revisionsData);
      revisionsSignatureRef.current = getRevisionsSignature(revisionsData);
      setProjectParties(partiesData);
      setProjectUsers(usersData);
      setRevisionNames(revisionNamesData);
      setProjectPmId(projectDetails?.project?.pm_id ?? null);
      setProjectStatus(projectDetails?.project?.status || '');
      setProjectType(projectDetails?.project?.project_type || '');
      setError(null);

      // Fetch workflows for each revision
      try {
        const workflowPromises = revisionsData.map(rev => projectsService.getRevisionWorkflow(Number(projectId), Number(documentId), rev.id));
        const workflows = await Promise.all(workflowPromises);
        const workflowsMap: Record<number, RevisionWorkflow> = {};
        revisionsData.forEach((rev, i) => {
          workflowsMap[rev.id] = workflows[i];
        });
        setRevisionWorkflows(workflowsMap);
      } catch (err) {
        console.error('Error loading workflows:', err);
      }

      try {
        const auxCountPromises = revisionsData.map((rev) =>
          projectsService
            .listRevisionAuxFiles(Number(projectId), Number(documentId), rev.id)
            .then((result) => result.files.length)
            .catch(() => 0)
        );
        const auxCounts = await Promise.all(auxCountPromises);
        const auxCountsMap: Record<number, number> = {};
        revisionsData.forEach((rev, index) => {
          auxCountsMap[rev.id] = auxCounts[index];
        });
        setRevisionAuxFileCounts(auxCountsMap);
      } catch (err) {
        console.error('Error loading auxiliary file counts:', err);
        setRevisionAuxFileCounts({});
      }
    } catch (err) {
      setError('Failed to load document revisions');
      console.error('Error loading data:', err);
    } finally {
      if (!silent) {
        setLoading(false);
      }
      isLoadingDataRef.current = false;
    }
  }, [documentId, getRevisionsSignature, projectId]);

  useEffect(() => {
    if (!projectId || !documentId) return;

    const checkForExternalRevisionUpdates = async () => {
      if (
        window.document.hidden ||
        isPollingRefreshRef.current ||
        isLoadingDataRef.current ||
        isActionSubmittingRef.current
      ) {
        return;
      }

      isPollingRefreshRef.current = true;
      try {
        const latestRevisions = await projectsService.getDocumentRevisions(
          Number(projectId),
          Number(documentId)
        );
        const latestSignature = getRevisionsSignature(latestRevisions);
        if (latestSignature !== revisionsSignatureRef.current) {
          await loadData(true);
        }
      } catch (err) {
        console.error('Error refreshing revisions:', err);
      } finally {
        isPollingRefreshRef.current = false;
      }
    };

    const handleVisibilityChange = () => {
      if (!window.document.hidden) {
        void checkForExternalRevisionUpdates();
      }
    };

    const intervalId = window.setInterval(() => {
      void checkForExternalRevisionUpdates();
    }, 15000);

    window.document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      window.clearInterval(intervalId);
      window.document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [documentId, getRevisionsSignature, loadData, projectId]);

  useEffect(() => {
    const handleOutsideCardsClick = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (cardsGridRef.current?.contains(target)) return;
      if (revisionActionButtonRef.current?.contains(target)) return;
      setSelectedRevisionCard(null);
    };

    window.document.addEventListener('mousedown', handleOutsideCardsClick);
    return () => {
      window.document.removeEventListener('mousedown', handleOutsideCardsClick);
    };
  }, []);

  const handleViewHistory = async (revisionId: number) => {
    try {
      setTimeseriesLoading(true);
      setTimeseriesModalOpen(true);
      const data = await projectsService.getDocumentAudit(Number(projectId), Number(documentId), revisionId);
      setAuditData(data);
    } catch (err) {
      console.error('Error loading audit data:', err);
    } finally {
      setTimeseriesLoading(false);
    }
  };

  const handleCloseTimeseriesModal = () => {
    setTimeseriesModalOpen(false);
    setAuditData([]);
  };

  const handleDownloadAuditFile = async (revisionId: number, versionId: number) => {
    setAuditDownloadContext({ revisionId, versionId });
    setAuditDownloadFiles([]);
    setAuditDownloadLoading(true);
    setAuditDownloadDialogOpen(true);
    try {
      const result = await projectsService.getRevisionVersionFiles(
        Number(projectId),
        Number(documentId),
        revisionId,
        versionId
      );
      const files: DownloadableFileItem[] = (result.files || []).map((f: { id: number; filename: string; category: string; size?: number; mime_type?: string; has_path?: boolean; processing_status?: string }) => ({
        id: f.id,
        filename: f.filename,
        category: f.category,
        size: f.size ?? 0,
        mime_type: f.mime_type,
        has_path: f.has_path,
        processing_status: f.processing_status,
        source: 'version' as const,
      }));
      setAuditDownloadFiles(files);
    } catch (err: any) {
      console.error('Error loading version files:', err);
      setError('Failed to load files for this version');
    } finally {
      setAuditDownloadLoading(false);
    }
  };

  const buildDownloadableFileList = useCallback(async (
    revisionId: number,
    versionId?: number
  ): Promise<DownloadableFileItem[]> => {
    let versionFiles: DownloadableFileItem[] = [];
    if (versionId) {
      const result = await projectsService.getRevisionVersionFiles(
        Number(projectId),
        Number(documentId),
        revisionId,
        versionId
      );

      versionFiles = (result.files || []).map((f: { id: number; filename: string; category: string; size?: number; mime_type?: string; has_path?: boolean; processing_status?: string }) => ({
          id: f.id,
          filename: f.filename,
          category: f.category,
          size: f.size ?? 0,
          mime_type: f.mime_type,
          has_path: f.has_path,
          processing_status: f.processing_status,
          source: 'version' as const,
        }));
    }

    let auxFiles: DownloadableFileItem[] = [];
    try {
      const auxResult = await projectsService.listRevisionAuxFiles(
        Number(projectId),
        Number(documentId),
        revisionId
      );
      auxFiles = (auxResult.files || []).map((f) => ({
        id: f.id,
        filename: f.filename,
        category: f.category,
        size: f.file_size ?? 0,
        mime_type: f.mime_type || undefined,
        has_path: true,
        source: 'aux' as const,
        description: f.description || undefined,
      }));
    } catch (auxErr) {
      console.warn('Could not load auxiliary files for download list', auxErr);
    }

    return [...versionFiles, ...auxFiles];
  }, [documentId, projectId]);

  const getRevisionFileBlob = async (
    revisionId: number,
    versionId: number | undefined,
    file: DownloadableFileItem
  ): Promise<Blob | null> => {
    if (file.source === 'aux') {
      return projectsService.getRevisionAuxFileBlob(
        Number(projectId),
        Number(documentId),
        revisionId,
        file.id
      );
    }
    if (versionId) {
      return projectsService.getRevisionFileBlob(
        Number(projectId),
        Number(documentId),
        revisionId,
        versionId,
        file.id
      );
    }
    return null;
  };

  const handleViewRevisionFile = async (
    revisionId: number,
    versionId: number | undefined,
    file: DownloadableFileItem
  ) => {
    if (file.processing_status && file.processing_status !== 'ready') {
      setError('PDF is still being stamped and is not ready for download.');
      return;
    }
    try {
      const blob = await getRevisionFileBlob(revisionId, versionId, file);
      if (!blob) {
        setError('Failed to open file');
        return;
      }
      openFilePreviewInNewTab(blob, file.filename, file.mime_type);
    } catch (err: unknown) {
      console.error('Error viewing file:', err);
      setError('Failed to open file');
    }
  };

  const handleDownloadRevisionFile = async (
    revisionId: number,
    versionId: number | undefined,
    file: DownloadableFileItem
  ) => {
    if (file.processing_status && file.processing_status !== 'ready') {
      setError('PDF is still being stamped and is not ready for download.');
      return;
    }
    try {
      const blob = await getRevisionFileBlob(revisionId, versionId, file);
      if (!blob) {
        setError('Failed to download file');
        return;
      }
      triggerBlobDownload(blob, file.filename, file.mime_type);
    } catch (err: unknown) {
      console.error('Error downloading file:', err);
      setError('Failed to download file');
    }
  };

  const handleDownloadRevisionFilesAsZip = async (
    revisionId: number,
    versionId: number | undefined,
    selectedFiles: DownloadableFileItem[]
  ) => {
    if (selectedFiles.length === 0 || zipDownloadingRevisionId !== null) return;

    setZipDownloadingRevisionId(revisionId);
    try {
      const zip = new JSZip();
      const truncateForZip = (name: string, maxBaseLength = 90) => {
        const dotIndex = name.lastIndexOf('.');
        const hasExtension = dotIndex > 0 && dotIndex < name.length - 1;
        const extension = hasExtension ? name.slice(dotIndex) : '';
        const base = hasExtension ? name.slice(0, dotIndex) : name;
        if (base.length <= maxBaseLength) return `${base}${extension}`;
        return `${base.slice(0, maxBaseLength - 3)}...${extension}`;
      };
      const visibleFiles = selectedFiles;
      if (visibleFiles.length === 0) {
        setError('No files are ready for download yet.');
        return;
      }

      const fileBlobs = await Promise.all(
        visibleFiles.map(async (file) => {
          const blob = await getRevisionFileBlob(revisionId, versionId, file);
          if (!blob) {
            throw new Error('Missing version for version-bound file');
          }
          const prefix = file.source === 'aux' ? 'aux_' : '';
          const safeFilename =
            sanitizeFilename(`${prefix}${file.filename}`) || `file-${file.id}`;
          const zipFilename = truncateForZip(safeFilename);
          return { filename: zipFilename, blob };
        })
      );

      await Promise.all(
        fileBlobs.map(async ({ filename, blob }) => {
          const content = await blob.arrayBuffer();
          zip.file(filename, content);
        })
      );

      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const zipUrl = window.URL.createObjectURL(zipBlob);
      const mainFile = selectedFiles.find(
        (f) => (f.category || '').toLowerCase() === 'main'
      );
      const sourceName =
        mainFile?.filename ||
        selectedFiles[0]?.filename ||
        `${document?.nd_number || 'document'}_files`;
      const nameWithoutExt = sourceName.includes('.')
        ? sourceName.slice(0, sourceName.lastIndexOf('.'))
        : sourceName;
      const zipNameBase = sanitizeFilename(nameWithoutExt) || 'document_files';
      const link = window.document.createElement('a');
      link.href = zipUrl;
      link.setAttribute('download', `${zipNameBase}.zip`);
      window.document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(zipUrl);
    } catch (err) {
      console.error('Error creating zip download:', err);
      setError('Failed to create ZIP download');
    } finally {
      setZipDownloadingRevisionId(null);
    }
  };

  const handleOpenActions = useCallback(async (revision: DocumentRevision) => {
    if (documentIsCancelled) {
      setError('This document is cancelled. Revision actions are locked.');
      return;
    }
    try {
      setSelectedRevision(revision);
      const workflow = await projectsService.getRevisionWorkflow(
        Number(projectId),
        Number(documentId),
        revision.id
      );
      setRevisionWorkflow(workflow);
      setActionsDialogOpen(true);
    } catch (err) {
      console.error('Error loading revision workflow:', err);
      setError('Failed to load revision workflow');
    }
  }, [documentIsCancelled, documentId, projectId]);

  const handleCloseActions = () => {
    setActionsDialogOpen(false);
    clearDeepLinkParams();
  };

  useEffect(() => {
    if (loading || revisions.length === 0) {
      return;
    }

    const revisionIdParam = searchParams.get('revisionId');
    const dialogParam = searchParams.get('dialog');
    if (!revisionIdParam) {
      return;
    }

    const linkKey = `${revisionIdParam}-${dialogParam ?? ''}`;
    if (deepLinkHandledRef.current === linkKey) {
      return;
    }

    const revisionIdNum = Number(revisionIdParam);
    const revision = revisions.find((item) => item.id === revisionIdNum);
    if (!revision) {
      return;
    }

    deepLinkHandledRef.current = linkKey;

    if (dialogParam === 'actions') {
      void handleOpenActions(revision);
    }
  }, [handleOpenActions, loading, revisions, searchParams]);

  const handleCreateRevision = async (
    revisionFields: Pick<
      CreateDocumentRevisionForm,
      'revision_name_id' | 'step' | 'start_date' | 'expected_issue_date'
    >
  ) => {
    if (projectIsClosed) {
      setError('This project is closed. Revision creation is locked.');
      return;
    }
    try {
      setCreatingRevision(true);
      const result = await projectsService.createDocumentRevision(Number(projectId), Number(documentId), {
        document_id: Number(documentId),
        revision_name_id: revisionFields.revision_name_id,
        step: revisionFields.step,
        start_date: revisionFields.start_date,
        expected_issue_date: revisionFields.expected_issue_date,
        status: 'pending',
        checkers: [],
        approvers: [],
        backup_parties: [],
      });
      setSuccessMessage('Revision created successfully');
      await loadData();
      return result.revision_id;
    } catch (err) {
      console.error('Error creating revision:', err);
      setError('Failed to create revision');
      throw err;
    } finally {
      setCreatingRevision(false);
    }
  };

  const handleEditRevision = async (
    revisionFields: Pick<
      CreateDocumentRevisionForm,
      'revision_name_id' | 'step' | 'start_date' | 'expected_issue_date'
    >
  ) => {
    if (projectIsClosed) {
      setError('This project is closed. Revision editing is locked.');
      return;
    }
    if (!editingRevision) return;

    try {
      setCreatingRevision(true);
      const workflowParties = await projectsService.getRevisionWorkflowParties(
        Number(projectId),
        Number(documentId),
        editingRevision.id
      );
      const author = workflowParties.find((party) => party.role === 'author');
      const checkers = workflowParties
        .filter((party) => party.role === 'checker')
        .map((party) => ({ party_id: party.party_id, step_order: party.step_order }));
      const approvers = workflowParties
        .filter((party) => party.role === 'approver')
        .map((party) => ({ party_id: party.party_id, step_order: party.step_order }));

      await projectsService.configureDocument(Number(projectId), Number(documentId), {
        document_id: Number(documentId),
        revision_name_id: editingRevision.revision_name_id,
        step: editingRevision.step || revisionFields.step,
        start_date: revisionFields.start_date,
        expected_issue_date: revisionFields.expected_issue_date,
        status: editingRevision.status || 'pending',
        author_party_id: author?.party_id,
        checkers,
        approvers,
        backup_parties: [],
      });
      setEditingRevision(null);
      setRevisionDialogInitialForm(null);
      setSuccessMessage('Revision updated successfully');
      await loadData();
      return editingRevision.id;
    } catch (err) {
      console.error('Error updating revision:', err);
      setError('Failed to update revision');
      throw err;
    } finally {
      setCreatingRevision(false);
    }
  };

  const handleOpenEditRevisionDialog = (revision: DocumentRevision) => {
    if (!canCreateDocumentRevision) {
      return;
    }
    if (projectIsClosed) {
      setError('This project is closed. Revision editing is locked.');
      return;
    }
    setEditingRevision(revision);
    setRevisionDialogInitialForm({
      revision_name_id: revision.revision_name_id,
      step: revision.step || 1,
      start_date: revision.start_date || '',
      expected_issue_date: revision.expected_issue_date || '',
    });
    setCreateRevisionDialogOpen(true);
  };

  const handleOpenEditWorkflowDialog = async (revision: DocumentRevision) => {
    if (!canEditRevisionWorkflow) {
      setError('You do not have permission to edit revision workflow.');
      return;
    }
    if (projectIsClosed) {
      setError('This project is closed. Workflow editing is locked.');
      return;
    }
    if ((revision.status || '').toLowerCase() === 'approved') {
      setError('Approved revisions cannot have their workflow edited.');
      return;
    }
    try {
      const workflowParties = await projectsService.getRevisionWorkflowParties(
        Number(projectId),
        Number(documentId),
        revision.id
      );
      const author = workflowParties.find((party) => party.role === 'author');
      const checkers = workflowParties
        .filter((party) => party.role === 'checker')
        .map((party) => ({ party_id: party.party_id, step_order: party.step_order }));
      const approvers = workflowParties
        .filter((party) => party.role === 'approver')
        .map((party) => ({ party_id: party.party_id, step_order: party.step_order }));

      setWorkflowRevision(revision);
      setWorkflowInitialForm({
        author_party_id: author?.party_id,
        checkers,
        approvers,
      });
      setWorkflowDialogOpen(true);
    } catch (err) {
      console.error('Error loading workflow:', err);
      setError('Failed to load revision workflow');
    }
  };

  const handleSaveWorkflow = async (workflow: RevisionWorkflowFormValues) => {
    if (!workflowRevision || !documentId) return;
    if (
      hasWorkflowParties(workflow) &&
      revisions.some(
        (revision) =>
          revision.id !== workflowRevision.id && isRevisionStarted(revision)
      )
    ) {
      setError(WORKFLOW_ASSIGNMENT_CONFLICT_ERROR);
      return;
    }
    try {
      setSavingWorkflow(true);
      const steps = workflowFormToSteps(workflow, projectParties);
      await projectsService.updateMDRRevision(
        Number(documentId),
        workflowRevision.id,
        workflowRevision.expected_issue_date || null,
        steps
      );
      setWorkflowDialogOpen(false);
      setWorkflowRevision(null);
      setWorkflowInitialForm(null);
      setSuccessMessage('Workflow updated successfully');
      await loadData();
    } catch (err) {
      console.error('Error saving workflow:', err);
      setError('Failed to update workflow');
    } finally {
      setSavingWorkflow(false);
    }
  };

  const handleCloseRevisionDialog = () => {
    setCreateRevisionDialogOpen(false);
    setEditingRevision(null);
    setRevisionDialogInitialForm(null);
  };

  const handleRevisionPrimaryAction = () => {
    if (!canCreateDocumentRevision) {
      return;
    }
    if (projectIsClosed) {
      setError('This project is closed. Create/Edit revision is locked.');
      return;
    }
    if (documentIsCancelled) {
      setError('This document is cancelled. Create/Edit revision is locked.');
      return;
    }
    if (selectedRevisionCard) {
      handleOpenEditRevisionDialog(selectedRevisionCard);
      return;
    }

    setEditingRevision(null);
    setRevisionDialogInitialForm(null);
    setCreateRevisionDialogOpen(true);
  };

  const handleValidate = async (comment: string, onBehalfOf?: number) => {
    if (!selectedRevision) return;
    isActionSubmittingRef.current = true;
    try {
      await projectsService.validateDocumentRevision(
        Number(projectId),
        Number(documentId),
        selectedRevision.id,
        { comment, on_behalf_of: onBehalfOf }
      );
      setSuccessMessage('Revision validated successfully');
      await loadData();
    } finally {
      isActionSubmittingRef.current = false;
    }
  };

  const handleReject = async (comment: string, file?: File, onBehalfOf?: number, attachments?: PendingAttachment[], archives?: File[], native_files?: File[], crs_files?: File[]) => {
    if (!selectedRevision) return;
    isActionSubmittingRef.current = true;
    try {
      await projectsService.rejectRevision(
        Number(projectId),
        Number(documentId),
        selectedRevision.id,
        { comment, file, attachments, archives, native_files, crs_files, on_behalf_of: onBehalfOf }
      );
      setSuccessMessage((file || (attachments && attachments.length) || (archives && archives.length) || (native_files && native_files.length) || (crs_files && crs_files.length)) ? 'Revision rejected and files uploaded' : 'Revision rejected');
      await loadData();
    } finally {
      isActionSubmittingRef.current = false;
    }
  };

  const handleUpload = async (files: { main?: File, attachments: PendingAttachment[], archives: File[], native_files: File[], crs_files: File[], tempFileId?: string, metadataPrecheckReuse?: MetadataPrecheckReusePayload }, notes: string, onBehalfOf?: number) => {
    if (!selectedRevision) return;
    isActionSubmittingRef.current = true;
    try {
      const response = await projectsService.updateRevisionVersion(
        Number(projectId),
        Number(documentId),
        {
          document_id: Number(documentId),
          revision_id: selectedRevision.id,
          file: files.main,
          notes,
          attachments: files.attachments,
          archives: files.archives,
          native_files: files.native_files,
          crs_files: files.crs_files,
          on_behalf_of: onBehalfOf,
          tempFileId: files.tempFileId,
          metadataPrecheckReuse: files.metadataPrecheckReuse,
        }
      );
      if (response.validation_warnings && response.validation_warnings.length > 0) {
        const compactWarnings = response.validation_warnings
          .map((w) => `${w.field}: expected ${w.expected}, detected ${w.detected}`)
          .join(' | ');
        setError(`Upload completed with metadata warnings: ${compactWarnings}`);
      } else if (response.pdf_stamping_deferred) {
        setSuccessMessage('Revision version uploaded successfully. PDF stamping will finish in the background for this large PDF.');
      } else if (response.metadata_validation_skip_reason === 'verify_metadata_manually') {
        setSuccessMessage('Revision version uploaded successfully. Please verify status, ND number, and revision manually on the first page.');
      } else {
        if (response.metadata_validation_executed) {
          setSuccessMessage('Revision version updated successfully (metadata comparison executed)');
        } else {
          const skipReason = response.metadata_validation_skip_reason || 'unknown';
          setSuccessMessage(`Revision version updated successfully (metadata comparison skipped: ${skipReason})`);
        }
      }
      await loadData();
    } finally {
      isActionSubmittingRef.current = false;
    }
  };

  const handleForward = async (backupPartyId: number, originalPartyId?: number) => {
    if (!selectedRevision) return;
    await projectsService.assignBackupParty(
      Number(projectId),
      Number(documentId),
      selectedRevision.id,
      backupPartyId,
      originalPartyId
    );
    setSuccessMessage('Backup assigned successfully');
    await loadData();
  };

  const handleCancelRevision = async () => {
    if (!selectedRevision) return;
    const status = (selectedRevision.status || '').toLowerCase();
    if (status === 'approved') {
      setError('Approved revisions cannot be cancelled.');
      return;
    }
    const confirmed = window.confirm(
      'This will permanently delete the selected revision and all of its uploaded files. Continue?'
    );
    if (!confirmed) return;

    await projectsService.cancelRevision(
      Number(projectId),
      Number(documentId),
      selectedRevision.id
    );
    setSuccessMessage('Revision cancelled and deleted successfully');
    await loadData();
  };



  const getUserRole = (workflow: RevisionWorkflow | null): 'author' | 'checker' | 'approver' | null => {
    if (!workflow || !workflow.user_party_id) return null;

    const revisionStatus = workflow.revision.status;
    const activeRole =
      revisionStatus === 'checking_pending'
        ? 'checker'
        : revisionStatus === 'approving_pending'
          ? 'approver'
          : revisionStatus === 'rejected' || revisionStatus === 'pending' || revisionStatus === 'draft'
            ? 'author'
            : null;

    if (activeRole) {
      // Author parties are stored with status 'completed', so only require 'pending' for checker/approver.
      const activeCandidates = workflow.parties.filter(
        p =>
          p.role === activeRole &&
          (activeRole === 'author' || p.status === 'pending') &&
          (p.party_id === workflow.user_party_id || p.backup_party === workflow.user_party_id)
      );
      if (activeCandidates.length > 0) {
        activeCandidates.sort((a, b) => a.step_order - b.step_order);
        return activeCandidates[0].role;
      }
    }

    // Fallback: keep previous behavior for non-active contexts.
    let userParty = workflow.parties.find(p => p.party_id === workflow.user_party_id);
    if (!userParty) {
      userParty = workflow.parties.find(p => p.backup_party === workflow.user_party_id);
    }
    return userParty?.role || null;
  };

  const canSendTransmittal = hasCapabilityOrProjectPm(user, CAP.SEND_TRANSMITTAL, projectPmId);
  const canCancelSelectedRevision = Boolean(
    selectedRevision &&
    hasCapabilityOrProjectPm(user, CAP.CANCEL_DOCUMENT, projectPmId) &&
    (selectedRevision.status || '').toLowerCase() !== 'approved'
  );

  const canUserAct = (workflow: RevisionWorkflow | null, user: User | null): { canValidate: boolean; canReject: boolean; canUpload: boolean; canForward: boolean } => {
    // 1. Calculate Controller Permissions
    const isDocumentController = hasCapabilityOrProjectPm(
      user,
      CAP.DOCUMENT_CONTROLLER_ACTIONS,
      projectPmId,
    );
    let controllerPermissions = { canValidate: false, canReject: false, canUpload: false, canForward: false };

    if (isDocumentController) {
      const active = workflow?.revision.status === 'checking_pending' || workflow?.revision.status === 'approving_pending';
      controllerPermissions = {
        canValidate: active,
        canReject: active,
        canUpload: workflow?.revision.status === 'pending' || workflow?.revision.status === 'rejected', // Allow upload if state permits
        canForward: true
      };
    }

    // 2. Calculate Workflow Permissions (Standard User Logic)
    let workflowPermissions = { canValidate: false, canReject: false, canUpload: false, canForward: false };

    if (workflow && workflow.user_party_id) {
      const revisionStatus = workflow.revision.status;
      const currentActiveRole =
        revisionStatus === 'checking_pending'
          ? 'checker'
          : revisionStatus === 'approving_pending'
            ? 'approver'
            : revisionStatus === 'rejected' || revisionStatus === 'pending' || revisionStatus === 'draft'
              ? 'author'
              : null;

      // Resolve the user party from the active phase first (important when same user has multiple roles,
      // e.g. an approver who is also the provisional/backup user for the author's upload task).
      // Note: author parties are stored with status 'completed', so the 'pending' filter only applies to checker/approver.
      let userParty = currentActiveRole
        ? workflow.parties
            .filter(
              p =>
                p.role === currentActiveRole &&
                (currentActiveRole === 'author' || p.status === 'pending') &&
                (p.party_id === workflow.user_party_id || p.backup_party === workflow.user_party_id)
            )
            .sort((a, b) => a.step_order - b.step_order)[0]
        : undefined;

      // Fallback to previous behavior when no active phase match exists.
      if (!userParty) {
        userParty = workflow.parties.find(p => p.party_id === workflow.user_party_id);
      }
      if (!userParty) {
        userParty = workflow.parties.find(p => p.backup_party === workflow.user_party_id);
      }

      if (userParty) {
        const role = userParty.role;
        const status = userParty.status;
        const stepOrder = userParty.step_order;
        const revisionStatus = workflow.revision.status;
        const isBackupUser = userParty.backup_party === workflow.user_party_id;

        // Check if a backup is assigned for this role - if so, only backup can act
        const hasBackupAssigned = workflow.parties.some(p => p.role === role && p.backup_party !== null);

        let blockedByBackup = false;
        if (hasBackupAssigned && !isBackupUser) {
          blockedByBackup = true;
        }

        if (!blockedByBackup) {
          // Upload is allowed for authors when revision is rejected or pending
          if (revisionStatus === 'rejected') {
            workflowPermissions.canUpload = role === 'author';
          } else if (revisionStatus === 'approved') {
            // No actions allowed
          } else {
            // Upload is allowed for authors when revision is rejected or pending
            workflowPermissions.canUpload = role === 'author' && (revisionStatus === 'rejected' || revisionStatus === 'pending');

            // Forward is allowed for authors, checkers, and approvers who have a role in the workflow
            workflowPermissions.canForward = role === 'author' || role === 'checker' || role === 'approver';

            // Check if a file has been uploaded
            const hasFile = !!workflow.latest_version;

            // Determine current active role based on revision status
            let activeRole: string;
            if (revisionStatus === 'checking_pending') {
              activeRole = 'checker';
            } else if (revisionStatus === 'approving_pending') {
              activeRole = 'approver';
            } else {
              activeRole = 'none'; // No active workflow
            }

            // Workflow validation/rejection only if roles match and it's their turn
            if (role === activeRole) {
              // Check if it's user's turn within their role (all previous steps must be validated)
              const previousStepsValidated = workflow.parties
                .filter(p => p.role === role && p.step_order < stepOrder)
                .every(p => p.status === 'validated');

              if (previousStepsValidated && hasFile) {
                if (role === 'checker' || role === 'approver') {
                  if (status === 'pending') {
                    workflowPermissions.canValidate = true;
                    workflowPermissions.canReject = true;
                  }
                }
              }
            }
          }
        }
      }
    }

    // 3. Merge Permissions
    const finalPermissions = {
      canValidate: controllerPermissions.canValidate || workflowPermissions.canValidate,
      canReject: controllerPermissions.canReject || workflowPermissions.canReject,
      canUpload: controllerPermissions.canUpload || workflowPermissions.canUpload,
      canForward: controllerPermissions.canForward || workflowPermissions.canForward,
    };

    return finalPermissions;
  };

  const getActiveParties = (workflow: RevisionWorkflow | null): DocumentParty[] => {
    if (!workflow) return [];
    const revisionStatus = workflow.revision.status;

    if (revisionStatus === 'approved' || revisionStatus === 'issued') return [];

    if (revisionStatus === 'rejected') {
      return workflow.parties.filter(p => p.role === 'author');
    }

    if (revisionStatus === 'draft' || !revisionStatus) { // pending/draft
      return workflow.parties.filter(p => p.role === 'author');
    }

    let currentActiveRole: string;
    if (revisionStatus === 'checking_pending') {
      currentActiveRole = 'checker';
    } else if (revisionStatus === 'approving_pending') {
      currentActiveRole = 'approver';
    } else {
      // Fallback for initial state that might be confusing
      return workflow.parties.filter(p => p.role === 'author');
    }

    const activeParties = workflow.parties.filter(p => p.role === currentActiveRole && p.status === 'pending');
    const validActiveParties = activeParties.filter(party => {
      const previousSteps = workflow.parties.filter(p => p.role === currentActiveRole && p.step_order < party.step_order);
      return previousSteps.every(p => p.status === 'validated');
    });

    return validActiveParties;
  };

  const getCurrentTurnText = (workflow: RevisionWorkflow | null): string => {
    if (!workflow) return '';
    const revisionStatus = workflow.revision.status;
    if (revisionStatus === 'approved') return 'Approved';
    if (revisionStatus === 'issued') return 'Issued';

    const activeParties = getActiveParties(workflow);

    if (activeParties.length === 0) {
      // Fallback text if we can't find specific parties but know phase
      if (revisionStatus === 'checking_pending') return 'Awaiting Checker Review';
      if (revisionStatus === 'approving_pending') return 'Awaiting Approver Review';
      return '';
    }

    const names = activeParties.map(party => {
      if (party.backup_party) {
        return (party as any).backup_party_name || party.name;
      }
      return party.name;
    }).join(', ');

    const roleMap: Record<string, string> = {
      'author': 'Originator',
      'checker': 'Checker',
      'approver': 'Approver'
    };
    const roleDisplay = roleMap[activeParties[0].role] || activeParties[0].role;

    if (activeParties[0].role === 'author') {
      if (revisionStatus === 'rejected') return `Rejected - Originator Action Required: ${names}`;
      return `Awaiting first upload by Originator: ${names}`;
    }

    return `Awaiting ${roleDisplay} Review: ${names}`;
  };

  const getRevisionStatusColor = (status?: string) => {
    switch (status) {
      case 'approved':
      case 'issued':
        return 'success';
      case 'checked':
      case 'checking_pending':
        return 'info';
      case 'approving_pending':
        return 'warning';
      case 'rejected':
        return 'error';
      case 'pending':
      default:
        return 'default';
    }
  };

  const getStorageIndicator = (filePath?: string) => {
    if (!filePath) return null;
    const isS3 = filePath.startsWith('s3://');
    return (
      <Tooltip title={isS3 ? "Stored in Cloud (S3)" : "Stored on Local Server"}>
        <Chip
          icon={isS3 ? <CloudIcon fontSize="small" /> : <StorageIcon fontSize="small" />}
          label={isS3 ? 'Cloud' : 'Server'}
          size="small"
          color={isS3 ? 'primary' : 'default'}
          variant="outlined"
          sx={{ ml: 1, borderColor: isS3 ? undefined : 'rgba(0, 0, 0, 0.23)', color: isS3 ? undefined : 'text.secondary', height: 24 }}
        />
      </Tooltip>
    );
  };


  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <LoadingAnimation />
      </Box>
    );
  }

  return (
    <Box>
      {/* Breadcrumbs */}
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" />}
        sx={{ mb: 3 }}
        aria-label="breadcrumb"
      >
        <MuiLink component={Link} to="/projects" underline="hover" color="inherit">
          Projects
        </MuiLink>
        <MuiLink component={Link} to={`/projects/${projectId}/documents`} underline="hover" color="inherit">
          Documents
        </MuiLink>
        <MuiLink component={Link} to={`/projects/${projectId}/documents`} underline="hover" color="inherit">
          {document?.title || `Document ${documentId}`}
        </MuiLink>
        <Typography color="text.primary">Revisions</Typography>
      </Breadcrumbs>

      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
        <Box display="flex" alignItems="center">
          <DescriptionIcon sx={{ mr: 2, color: isDark ? NAUTILUS.sand : NAUTILUS.deepOcean, fontSize: '2rem' }} />
          <Box>
            <Typography
              variant="h4"
              component="h1"
              sx={{
                fontWeight: 600,
                letterSpacing: '-0.01em',
                color: isDark ? '#ffffff' : NAUTILUS.deepOcean,
              }}
            >
              Document Revisions
            </Typography>
            <Typography
              variant="subtitle1"
              sx={{
                color: isDark ? '#b0bec5' : '#546e7a',
                fontFamily: 'monospace',
                letterSpacing: '0.03em',
              }}
            >
              {document?.nd_number} - {document?.title}
            </Typography>
          </Box>
        </Box>
        {canCreateDocumentRevision && (
          <Box ref={revisionActionButtonRef}>
            <Tooltip title={projectIsClosed ? 'Project is closed. Revision creation/editing is locked.' : ''}>
              <span>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={selectedRevisionCard ? <EditIcon /> : <CreateIcon />}
                  onClick={handleRevisionPrimaryAction}
                  disabled={documentIsCancelled || projectIsClosed}
                  sx={{
                    minWidth: '170px',
                    bgcolor: NAUTILUS.sand,
                    color: NAUTILUS.deepOcean,
                    fontWeight: 600,
                    '&:hover': {
                      bgcolor: alpha(NAUTILUS.sand, 0.85),
                    },
                  }}
                >
                  {selectedRevisionCard ? 'Edit Revision' : 'Create Revision'}
                </Button>
              </span>
            </Tooltip>
          </Box>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      {documentIsCancelled && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          This document is cancelled. Actions and revision creation/editing are locked.
        </Alert>
      )}
      {projectIsClosed && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          This project is closed. Revision creation/editing is locked.
        </Alert>
      )}

      <Grid ref={cardsGridRef} container spacing={2} sx={{ minHeight: '200px' }}>
        {revisions.map((revision) => (
          <Grid item xs={12} sm={6} md={4} key={revision.id}>
            <Card
              onClick={() => setSelectedRevisionCard(revision)}
              sx={{
                position: 'relative',
                height: '100%',
                border: selectedRevisionCard?.id === revision.id
                  ? `2px solid ${isDark ? NAUTILUS.sand : NAUTILUS.deepOcean}`
                  : `1px solid ${panelBorder}`,
                borderRadius: 1,
                boxShadow: 'none',
                bgcolor: surfaceBg,
                transition: 'all 0.15s ease',
                '&:hover': {
                  borderColor: isDark ? NAUTILUS.sand : NAUTILUS.deepOcean,
                },
              }}
            >
              <Chip
                label={`R${revision.revision_counter ?? revision.step ?? '-'}`}
                size="small"
                sx={{
                  position: 'absolute',
                  top: 10,
                  right: 10,
                  fontWeight: 700,
                  color: isDark ? NAUTILUS.sand : NAUTILUS.deepOcean,
                  border: `1px solid ${isDark ? alpha(NAUTILUS.sand, 0.45) : alpha(NAUTILUS.deepOcean, 0.25)}`,
                  bgcolor: isDark ? alpha(NAUTILUS.sand, 0.08) : alpha(NAUTILUS.deepOcean, 0.06),
                  zIndex: 1,
                }}
              />
              <CardContent>
                <Typography
                  variant="h6"
                  component="div"
                  gutterBottom
                  sx={{
                    fontWeight: 600,
                    color: isDark ? '#ffffff' : NAUTILUS.deepOcean,
                  }}
                >
                  {revision.revision_name ? `${revision.revision_name}${revision.step || 1}` : 'Unnamed Revision'}
                </Typography>
                <Box display="flex" alignItems="center" mb={1}>
                  <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                    Status:
                  </Typography>
                  {revision.status ? (
                    <Chip
                      label={revision.status}
                      size="small"
                      color={getRevisionStatusColor(revision.status)}
                    />
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      -
                    </Typography>
                  )}
                  {getStorageIndicator(revision.file_path)}
                </Box>
                {revisionWorkflows[revision.id] && (
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {getCurrentTurnText(revisionWorkflows[revision.id])}
                  </Typography>
                )}
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Issued Date: {revision.issued_date ? new Date(revision.issued_date).toLocaleDateString() : '-'}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Expected Issue Date: {revision.expected_issue_date ? new Date(revision.expected_issue_date).toLocaleDateString() : '-'}
                </Typography>
                {(revisionWorkflows[revision.id]?.latest_version ||
                  (revisionAuxFileCounts[revision.id] ?? 0) > 0) && (
                  <RevisionCardFiles
                    revisionId={revision.id}
                    versionId={revisionWorkflows[revision.id]?.latest_version?.version_id}
                    projectId={Number(projectId)}
                    documentId={Number(documentId)}
                    loadFiles={buildDownloadableFileList}
                    onView={(file) =>
                      void handleViewRevisionFile(
                        revision.id,
                        revisionWorkflows[revision.id]?.latest_version?.version_id,
                        file
                      )
                    }
                    onDownload={(file) =>
                      void handleDownloadRevisionFile(
                        revision.id,
                        revisionWorkflows[revision.id]?.latest_version?.version_id,
                        file
                      )
                    }
                    onDownloadAll={(selectedFiles) =>
                      handleDownloadRevisionFilesAsZip(
                        revision.id,
                        revisionWorkflows[revision.id]?.latest_version?.version_id,
                        selectedFiles
                      )
                    }
                    isDownloadingZip={zipDownloadingRevisionId === revision.id}
                  />
                )}
                <Stack direction="row" spacing={0.75} mt={2} flexWrap="wrap" useFlexGap>
                  <Tooltip title="View audit history">
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<HistoryIcon />}
                      sx={revisionCardButtonSx}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewHistory(revision.id);
                      }}
                    >
                      History
                    </Button>
                  </Tooltip>
                  <Tooltip
                    title={
                      !canEditRevisionWorkflow
                        ? 'Only admin, director or the project manager can edit revision workflow'
                        : (revision.status || '').toLowerCase() === 'approved'
                          ? 'Approved revisions cannot have their workflow edited'
                          : projectIsClosed
                            ? 'Project is closed. Workflow editing is locked.'
                            : documentIsCancelled
                              ? 'Document is cancelled. Workflow editing is locked.'
                              : 'Edit revision workflow'
                    }
                  >
                    <span>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<WorkflowIcon />}
                        sx={revisionCardButtonSx}
                        disabled={
                          !canEditRevisionWorkflow ||
                          documentIsCancelled ||
                          projectIsClosed ||
                          (revision.status || '').toLowerCase() === 'approved'
                        }
                        onClick={(e) => {
                          e.stopPropagation();
                          void handleOpenEditWorkflowDialog(revision);
                        }}
                      >
                        Workflow
                      </Button>
                    </span>
                  </Tooltip>
                  <Tooltip title="Perform actions on this revision">
                    <span>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<ActionIcon />}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenActions(revision);
                        }}
                        disabled={documentIsCancelled}
                        sx={{
                          ...revisionCardButtonSx,
                          bgcolor: NAUTILUS.sand,
                          color: NAUTILUS.deepOcean,
                          fontWeight: 600,
                          '&:hover': {
                            bgcolor: alpha(NAUTILUS.sand, 0.85),
                          },
                        }}
                      >
                        Actions
                      </Button>
                    </span>
                  </Tooltip>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {revisions.length === 0 && !loading && (
        <Box textAlign="center" py={8}>
          <TimelineIcon sx={{ fontSize: '4rem', color: (theme) => theme.palette.mode === 'dark' ? 'grey.400' : 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No revisions found for this document
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Revisions will appear here once they are created
          </Typography>
        </Box>
      )}

      {/* Enhanced Document Audit History Modal */}
      <Dialog
        open={timeseriesModalOpen}
        onClose={handleCloseTimeseriesModal}
        maxWidth="lg"
        fullWidth
        TransitionComponent={Fade}
        TransitionProps={{ timeout: 500 }}
        PaperProps={{
          sx: {
            bgcolor: surfaceBg,
            border: `1px solid ${panelBorder}`,
            borderRadius: 1,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: `1px solid ${panelBorder}`,
            py: 1.5,
          }}
        >
          <Box display="flex" alignItems="center">
            <Avatar sx={{ bgcolor: isDark ? alpha(NAUTILUS.sand, 0.15) : alpha(NAUTILUS.deepOcean, 0.1), mr: 2 }}>
              <TimelineIcon sx={{ color: isDark ? NAUTILUS.sand : NAUTILUS.deepOcean }} />
            </Avatar>
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  letterSpacing: '-0.01em',
                  color: isDark ? '#ffffff' : NAUTILUS.deepOcean,
                }}
              >
                Document Audit History
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: isDark ? '#b0bec5' : '#546e7a',
                  fontFamily: 'monospace',
                }}
              >
                {document?.nd_number} - {document?.title}
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={handleCloseTimeseriesModal}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {timeseriesLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
              <LoadingAnimation />
            </Box>
          ) : auditData.length > 0 ? (
            <Box sx={{ p: 3 }}>
              <Timeline position="alternate" sx={{ p: 0 }}>
                {auditData.map((audit, index) => {
                  const getActionIcon = (action: string) => {
                    switch (action.toLowerCase()) {
                      case 'created':
                        return <CreateIcon />;
                      case 'updated':
                      case 'edited':
                        return <EditIcon />;
                      case 'deleted':
                        return <DeleteIcon />;
                      case 'uploaded':
                        return <UploadIcon />;
                      default:
                        return <HistoryIcon />;
                    }
                  };

                  const getActionColor = (action: string) => {
                    switch (action.toLowerCase()) {
                      case 'created':
                        return 'success';
                      case 'updated':
                      case 'edited':
                        return 'info';
                      case 'deleted':
                        return 'error';
                      case 'uploaded':
                        return 'warning';
                      default:
                        return 'primary';
                    }
                  };

                  return (
                    <TimelineItem key={index}>
                      <TimelineSeparator>
                        <TimelineDot
                          color={getActionColor(audit.action)}
                          sx={{
                            boxShadow: 2,
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              transform: 'scale(1.1)',
                              boxShadow: 4,
                            },
                          }}
                        >
                          {getActionIcon(audit.action)}
                        </TimelineDot>
                        {index < auditData.length - 1 && (
                          <TimelineConnector sx={{ bgcolor: 'grey.300', height: 40 }} />
                        )}
                      </TimelineSeparator>
                      <TimelineContent sx={{ py: '12px', px: 2 }}>
                        <Fade in={true} timeout={500 + index * 100}>
                          <Card
                            elevation={2}
                            sx={{
                              borderRadius: 2,
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                elevation: 6,
                                transform: 'translateY(-2px)',
                              },
                            }}
                          >
                            <CardContent sx={{ pb: '16px !important' }}>
                              <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                                <Box display="flex" alignItems="center" flex={1}>
                                  <Chip
                                    label={audit.action}
                                    size="small"
                                    color={getActionColor(audit.action)}
                                    sx={{ mr: 2, fontWeight: 600 }}
                                  />
                                  <Box display="flex" alignItems="center" sx={{ opacity: 0.7 }}>
                                    <ScheduleIcon sx={{ fontSize: 16, mr: 0.5 }} />
                                    <Typography variant="caption">
                                      {audit.action_time ? new Date(audit.action_time).toLocaleString() : 'Unknown time'}
                                    </Typography>
                                  </Box>
                                </Box>
                                {audit.version_id && audit.file_path && (
                                  <Box display="flex" alignItems="center">
                                    {getStorageIndicator(audit.file_path)}
                                    <Tooltip title="Download file">
                                      <IconButton
                                        size="small"
                                        color="primary"
                                        onClick={() => handleDownloadAuditFile(audit.revision_id!, audit.version_id!)}
                                      >
                                        <DownloadIcon />
                                      </IconButton>
                                    </Tooltip>
                                  </Box>
                                )}
                              </Box>

                              <Box display="flex" alignItems="center" mb={1}>
                                <PersonIcon sx={{ fontSize: 18, mr: 1, color: 'text.secondary' }} />
                                <Typography variant="body2" color="text.secondary">
                                  {audit.uploaded_by || 'Unknown user'}
                                  {audit.trigram && ` (${audit.trigram})`}
                                </Typography>
                              </Box>

                              {audit.revision_name && audit.revision_step && (
                                <Box display="flex" alignItems="center" mb={1}>
                                  <Typography variant="body2" sx={{ fontWeight: 500, mr: 1 }}>
                                    Revision: {audit.revision_counter} ({audit.revision_name} {audit.revision_step})
                                  </Typography>
                                </Box>
                              )}

                              {audit.action_details && (
                                <Box sx={{ mt: 1, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                                  <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                    {audit.action_details}
                                  </Typography>
                                </Box>
                              )}
                            </CardContent>
                          </Card>
                        </Fade>
                      </TimelineContent>
                    </TimelineItem>
                  );
                })}
              </Timeline>
            </Box>
          ) : (
            <Box textAlign="center" py={8} px={3}>
              <Avatar sx={{ bgcolor: 'grey.100', width: 80, height: 80, mx: 'auto', mb: 3 }}>
                <TimelineIcon sx={{ fontSize: '2.5rem', color: 'grey.400' }} />
              </Avatar>
              <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontWeight: 500 }}>
                No Audit History Available
              </Typography>
              <Typography variant="body2" color="text.secondary">
                This document doesn't have any recorded audit activities yet.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${panelBorder}` }}>
          <Button
            onClick={handleCloseTimeseriesModal}
            variant="contained"
            sx={{
              bgcolor: NAUTILUS.sand,
              color: NAUTILUS.deepOcean,
              fontWeight: 600,
              borderRadius: 1,
              px: 3,
              '&:hover': {
                bgcolor: alpha(NAUTILUS.sand, 0.85),
              },
            }}
          >
            Close History
          </Button>
        </DialogActions>
      </Dialog>

      <DownloadSelectionDialog
        open={auditDownloadDialogOpen}
        onClose={() => setAuditDownloadDialogOpen(false)}
        files={auditDownloadFiles}
        loading={auditDownloadLoading}
        onView={(file) => {
          if (auditDownloadContext) {
            void handleViewRevisionFile(auditDownloadContext.revisionId, auditDownloadContext.versionId, file);
          }
        }}
        onDownload={(file) => {
          if (auditDownloadContext) {
            void handleDownloadRevisionFile(auditDownloadContext.revisionId, auditDownloadContext.versionId, file);
          }
        }}
        onDownloadAll={(selectedFiles) => {
          if (auditDownloadContext) {
            return handleDownloadRevisionFilesAsZip(auditDownloadContext.revisionId, auditDownloadContext.versionId, selectedFiles);
          }
        }}
        isDownloadingAll={auditDownloadContext ? zipDownloadingRevisionId === auditDownloadContext.revisionId : false}
      />

      {/* Revision Actions Dialog */}
      {selectedRevision && revisionWorkflow && (
        <RevisionActionsDialog
          open={actionsDialogOpen}
          onClose={handleCloseActions}
          onValidate={handleValidate}
          onReject={handleReject}
          onUpload={handleUpload}
          onForward={handleForward}
          onCancelRevision={handleCancelRevision}
          onTransmittal={async (mainRecipients, otherRecipients, fileIds, message, expiresInDays) => {
            if (!selectedRevision) return;
            await projectsService.sendTransmittal(
              Number(projectId), Number(documentId), selectedRevision.id,
              {
                main_recipients: mainRecipients,
                other_recipients: otherRecipients,
                file_ids: fileIds,
                message,
                expires_in_days: expiresInDays
              }
            );
            setSuccessMessage('Transmittal sent successfully');
            // Refresh revisions to reflect new status
            const revs = await projectsService.getDocumentRevisions(Number(projectId), Number(documentId));
            setRevisions(revs);
          }}
          userRole={getUserRole(revisionWorkflow)}
          canValidate={canUserAct(revisionWorkflow, user).canValidate}
          canReject={canUserAct(revisionWorkflow, user).canReject}
          canUpload={canUserAct(revisionWorkflow, user).canUpload}
          canForward={canUserAct(revisionWorkflow, user).canForward}
          canCancelRevision={canCancelSelectedRevision}
          canSendTransmittal={canSendTransmittal}
          isDocumentController={hasCapabilityOrProjectPm(user, CAP.DOCUMENT_CONTROLLER_ACTIONS, projectPmId)}
          projectId={Number(projectId)}
          documentId={Number(documentId)}
          revisionId={selectedRevision.id}
          revisionStatus={revisionWorkflow.revision.status}
          downloadParams={revisionWorkflow.latest_version ? {
            projectId: Number(projectId),
            documentId: Number(documentId),
            revisionId: selectedRevision.id,
            versionId: revisionWorkflow.latest_version.version_id,
            document
          } : undefined}
          parties={revisionWorkflow.parties}
          projectParties={projectParties}
          userPartyId={revisionWorkflow.user_party_id || 0}
          projectUsers={projectUsers}
          activeParties={getActiveParties(revisionWorkflow)}
          isProposalProject={isProposalProjectType(projectType)}
        />
      )}

      {/* Success Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={() => setSuccessMessage(null)}
        message={successMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
      <RevisionDialog
        open={createRevisionDialogOpen}
        onClose={handleCloseRevisionDialog}
        projectId={Number(projectId)}
        documentId={Number(documentId)}
        editingRevisionId={editingRevision?.id ?? null}
        documentTitle={document?.title || ''}
        revisionNames={revisionNames}
        onSubmit={editingRevision ? handleEditRevision : handleCreateRevision}
        loading={creatingRevision}
        disabled={projectIsClosed || documentIsCancelled}
        nextRevisionCounter={
          editingRevision
            ? (editingRevision.revision_counter ?? 0)
            : revisions.length > 0
              ? Math.max(...revisions.map((r) => r.revision_counter ?? 0)) + 1
              : 0
        }
        initialRevision={revisionDialogInitialForm}
        dialogTitle={editingRevision ? 'Edit Document Revision' : 'Create Document Revision'}
        submitLabel={editingRevision ? 'Save Changes' : 'Create Revision'}
        submittingLabel={editingRevision ? 'Saving...' : 'Creating...'}
        lockIdentity={!!editingRevision}
        lockDates={!!editingRevision && editingRevision.metadata_edit_locked === true}
      />

      <WorkflowDialog
        open={workflowDialogOpen}
        onClose={() => {
          setWorkflowDialogOpen(false);
          setWorkflowRevision(null);
          setWorkflowInitialForm(null);
        }}
        documentTitle={document?.title || ''}
        revisionLabel={
          workflowRevision
            ? `${workflowRevision.revision_name || ''}${workflowRevision.step || 1}`
            : undefined
        }
        parties={projectParties}
        initialWorkflow={workflowInitialForm}
        recreateWorkflow={lastDocumentWorkflowTemplate?.workflow ?? null}
        recreateWorkflowSource={lastDocumentWorkflowTemplate?.sourceLabel}
        onSubmit={handleSaveWorkflow}
        loading={savingWorkflow}
      />
    </Box>
  );
};

export default DocumentRevisionsPage;